import {memo, useCallback} from 'react';

import {view} from '@sail/ui';

import {
  CloseConfirmationModal,
  useApp,
  useConversationExceptions,
  useConversationInlineDataEvents,
  useSetAppState,
} from '@stripe-internal/conversations-assistant';
import {Conversation} from '@stripe-internal/conversations-client';
import {ConversationActivityIndicatorProvider} from '@stripe-internal/conversations-ui';

import {MobileAssistantInput} from './MobileAssistantInput';
import {MobileAssistantLog} from './MobileAssistantLog';

export const MobileConversationScreen = memo(function ConversationScreen({
  conversation,
}: {
  conversation: Conversation;
}) {
  const app = useApp();
  const setAppState = useSetAppState();

  useConversationExceptions(conversation);
  useConversationInlineDataEvents(conversation);

  const confirmEndChat = useCallback(async () => {
    setAppState((state) => {
      state.showCloseConfirmation = false;
    });
    try {
      await app.endConversation(conversation);
      setAppState((state) => {
        state.error = null;
      });
    } catch {
      setAppState((state) => {
        // endConversation can throw a non-fatal error
        // Clear currentConversationId to navigate away from the dead screen,
        // and clear any stale error so it isn't shown on the next screen.
        state.currentConversationId = null;
        state.error = null;
      });
    }
  }, [app, conversation, setAppState]);

  return (
    <view.div
      data-testid="conversation-screen"
      css={{
        stack: 'y',
        height: 'fill',
        position: 'relative',
      }}
      // Setting the `aria-live` attribute so that screen readers can read new messages coming in.
      // We set it to `polite` so that new message don't interrupt the reading of previous messages.
      aria-live="polite"
    >
      <CloseConfirmationModal onClose={confirmEndChat} />
      <ConversationActivityIndicatorProvider>
        <view.div css={{flex: '1 1 auto', minHeight: 0, overflow: 'hidden'}}>
          <MobileAssistantLog conversation={conversation} />
        </view.div>
        <view.div css={{flex: '0 0 auto'}}>
          <MobileAssistantInput conversation={conversation} />
        </view.div>
      </ConversationActivityIndicatorProvider>
    </view.div>
  );
});
